import { FC, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { DetailedError } from '@/types/error.types';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import { useEditMaintenanceFreeze, useEditNationalProgram } from '@/services/bundles/bundles.service';
import { NationalProgramStates } from '@/types/nationalProgram.types';
import { FreezeFormErrors, FreezeFormValues } from '../CreateFreeze/CreateFreeze.types';
import { FreezeStepOne } from '../FormFreezeWizard/FreezeStepOne';
import { FreezeStepThree } from '../FormFreezeWizard/FreezeStepThree';
import { FreezeStepTwo } from '../FormFreezeWizard/FreezeStepTwo';
import { EditFreezeProps, EditMaintenanceFreezeFormValues } from './EditFreeze.types';

const EMPTY_FORM_ERRORS = {} as FreezeFormErrors;
const EditFreeze: FC<EditFreezeProps> = ({
  bundleData,
  freezeId,
  name,
  optInStartDate,
  startDate,
  endDate,
  freezeType,
  state,
  nationalDefaultPrice,
  nationalPricePoints,
  optOutHidden,
  redeemableOnline,
  visibleOnline,
  sustainPricingFlag,
  isOpen,
  handleClose,
  setConfirmationModalVisible,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<StepCount>(0);
  const [changesExist, setChangesExist] = useState(false);

  const [validStepOne, setValidStepOne] = useState(false);
  const [validStepTwo, setValidStepTwo] = useState(false);

  const [formValues, setFormValues] = useState<FreezeFormValues>({
    freezeName: name,
    freezeType,
    freezeStartDate: startDate,
    freezeEndDate: endDate,
    optInStartDate,
    defaultPrice: nationalDefaultPrice?.toFixed(2),
    nationalPricePointTwo: nationalPricePoints?.[0] ? nationalPricePoints?.[0]?.toFixed(2) : '',
    nationalPricePointThree: nationalPricePoints?.[1] ? nationalPricePoints?.[1]?.toFixed(2) : '',
    nationalPricePointFour: nationalPricePoints?.[2] ? nationalPricePoints?.[2]?.toFixed(2) : '',
    optOutHidden,
    redeemableOnline,
    visibleOnline,
    sustainPricingFlag: sustainPricingFlag ?? false,
  });

  const [initialFormValues, setInitialFormValues] = useState<EditMaintenanceFreezeFormValues>({
    freezeName: name,
    freezeStartDate: startDate,
    freezeEndDate: endDate,
  });

  const [formErrors, setFormErrors] = useState<FreezeFormErrors>({
    freezeName: '',
    optInStartDate: '',
    freezeStartDate: '',
    freezeEndDate: '',
    defaultPrice: '',
    nationalPricePointTwo: '',
    nationalPricePointThree: '',
    nationalPricePointFour: '',
    optOutHidden: '',
    redeemableOnline: '',
    visibleOnline: '',
  });

  const dateTomorrow = new Date();
  dateTomorrow.setDate(dateTomorrow.getDate() + 1);

  const nationalPrices = [
    formValues.nationalPricePointTwo,
    formValues.nationalPricePointThree,
    formValues.nationalPricePointFour,
  ];

  const filteredNationalPrices = nationalPrices
    .filter((price): price is string => !!price)
    .map((price) => Number(price))
    .filter((price) => Number.isFinite(price));

  const onClose = () => {
    setStep(0);
    handleClose();
  };

  const { mutate: updateMaintenanceFreeze, isLoading: updateMaintenanceFreezeIsLoading } =
    useEditMaintenanceFreeze(freezeId);

  const { mutate: updateNationalProgramFreeze, isLoading: updateNationalProgramFreezeIsLoading } =
    useEditNationalProgram(freezeId);

  const handleUpdateMaintenanceFreeze = () => {
    updateMaintenanceFreeze(
      {
        freezeName: formValues.freezeName,
        startDate: formValues.freezeStartDate,
        endDate: formValues.freezeEndDate,
      },
      {
        onSuccess: () => {
          handleClose();
          setInitialFormValues({ ...formValues });
          setChangesExist(false);
          setConfirmationModalVisible(true);
          queryClient.invalidateQueries(['freezes']);
        },
        onError: (error) => {
          if ((error as AxiosError).message === 'Request failed with status code 422') {
            showToast({
              id: 'errorFreezeOverlap',
              label: t('error.freezeOverlap'),
              toastType: ToastTypes.Error,
            });
          }
          if (error as DetailedError) {
            // TODO we need to send by BE a error code to avoid i18n there
            const e = error as DetailedError;
            console.warn('Error trying to save freeze: ', e.message, e.details);
            showToast({
              id: 'nationalProgramOverlap',
              label: t('error.freezeOverlap'),
              toastType: ToastTypes.Error,
            });
          } else {
            showToast({
              id: 'errorUpdatingMaintenanceFreeze',
              label: t('error.updatingMaintenanceFreeze'),
              toastType: ToastTypes.Error,
            });
          }
        },
      },
    );
  };

  const handleUpdateNationalProgramFreeze = () => {
    let requestBody = {};
    const sustainPricingFlagValue = formValues.sustainPricingFlag === true;
    if (state === NationalProgramStates.LIVE) {
      requestBody = {
        freezeName: formValues.freezeName,
        freezeEndDate: formValues.freezeEndDate,
        sustainPricingFlag: sustainPricingFlagValue,
      };
    } else if (state === NationalProgramStates.OPT_IN) {
      requestBody = {
        freezeName: formValues.freezeName,
        freezeStartDate: formValues.freezeStartDate,
        freezeEndDate: formValues.freezeEndDate,
        sustainPricingFlag: sustainPricingFlagValue,
      };
    } else if (state === NationalProgramStates.NA) {
      requestBody = {
        freezeName: formValues.freezeName,
        optInStartDate: formValues.optInStartDate,
        freezeStartDate: formValues.freezeStartDate,
        freezeEndDate: formValues.freezeEndDate,
        defaultPrice: +formValues.defaultPrice,
        pricePoints: filteredNationalPrices,
        optOutHidden: formValues.optOutHidden,
        redeemableOnline: formValues.redeemableOnline,
        visibleOnline: formValues.visibleOnline,
        sustainPricingFlag: sustainPricingFlagValue,
      };
    }
    updateNationalProgramFreeze(requestBody, {
      onSuccess: () => {
        handleClose();
        setStep(0);

        setConfirmationModalVisible(true);
        queryClient.invalidateQueries(['freezes']);
      },
      onError: (error) => {
        if ((error as AxiosError).message === 'Request failed with status code 422') {
          showToast({
            id: 'errorFreezeOverlap',
            label: t('error.freezeOverlap'),
            toastType: ToastTypes.Error,
          });
        } else if (error as DetailedError) {
          // TODO we need to send by BE a error code to avoid i18n there
          const e = error as DetailedError;
          console.warn('Error trying to save freeze: ', e.message, e.details);
          showToast({
            id: 'nationalProgramOverlap',
            label: t('error.freezeOverlap'),
            toastType: ToastTypes.Error,
          });
        } else {
          showToast({
            id: 'errorUpdatingNationalProgramFreeze',
            label: t('error.updatingNationalProgramFreeze'),
            toastType: ToastTypes.Error,
          });
        }
      },
    });
  };

  useEffect(() => {
    let changeExists = false;
    if (
      formValues.freezeName !== initialFormValues.freezeName ||
      formValues.freezeStartDate !== initialFormValues.freezeStartDate ||
      formValues.freezeEndDate !== initialFormValues.freezeEndDate
    ) {
      changeExists = true;
    }
    setChangesExist(changeExists);
  }, [formValues, initialFormValues]);

  // reset form errors when form is closed
  useEffect(() => {
    if (isOpen === false) {
      setFormErrors(EMPTY_FORM_ERRORS);
    }
  }, [isOpen]);
  const handleStepOneNext = () => {
    setFormErrors(EMPTY_FORM_ERRORS);
    setStep(1);
  };

  // Price points and online settings are fixed once opt-in opens, so those programs
  // go straight from the editable dates to review.
  const hideStepTwo = state === NationalProgramStates.LIVE || state === NationalProgramStates.OPT_IN;

  const stepOne = {
    content: (
      <FreezeStepOne
        editing
        state={state}
        initialFormData={formValues}
        onChangeFreezeData={(data: FreezeFormValues, isValid) => {
          setFormValues({ ...formValues, ...data });
          setValidStepOne(isValid);
        }}
      />
    ),
    title: t('editFreezeModal.nationalProgram.stepOne.title'),
    description: t('editFreezeModal.nationalProgram.stepOne.description'),
    onBackClick: onClose,
    onNextClick: formValues.freezeType === 'NATIONAL_PROGRAM' ? handleStepOneNext : handleUpdateMaintenanceFreeze,
    nextButtonIsLoading: updateMaintenanceFreezeIsLoading,
    nextButtonText: formValues.freezeType === 'MAINTENANCE' ? t('common.confirm') : t('common.next'),
    nextButtonDisabled: !validStepOne,
  };

  const stepTwo = {
    content: (
      <FreezeStepTwo
        editing
        state={state}
        bundle={bundleData}
        initialFormData={formValues}
        onChangeFreezeData={(data: FreezeFormValues, isValid) => {
          setFormValues({ ...formValues, ...data });
          setValidStepTwo(isValid);
        }}
      />
    ),
    title: t('editFreezeModal.nationalProgram.stepTwo.title'),
    description: t('editFreezeModal.nationalProgram.stepTwo.description'),
    onBackClick: () => setStep(0),
    onNextClick: () => setStep(2),
    nextButtonDisabled: !validStepTwo,
  };

  const reviewStep = {
    content: <FreezeStepThree editing bundle={bundleData} initialFormData={formValues} />,
    title: t('editFreezeModal.nationalProgram.stepThree.title'),
    description: t('editFreezeModal.nationalProgram.stepThree.description'),
    onBackClick: () => setStep(hideStepTwo ? 0 : 1),
    onNextClick: handleUpdateNationalProgramFreeze,
    nextButtonIsLoading: updateNationalProgramFreezeIsLoading,
    nextButtonText: t('common.confirm'),
  };

  return (
    <Wizard
      currentStep={step}
      showStepIndicator={formValues?.freezeType === 'NATIONAL_PROGRAM'}
      steps={hideStepTwo ? [stepOne, reviewStep] : [stepOne, stepTwo, reviewStep]}
      isOpen={isOpen}
      handleClose={onClose}
    />
  );
};

export default EditFreeze;
